import React, { useState, useEffect } from 'react';
import { useCalendar } from '../../../../context/CalendarContext';
import { useAuth } from '../../../../context/AuthContext';
import { formatDisplayDate, toCentralTime } from '../../../../lib/utils/dateTimeFormat';
import { NextArrow, PrevArrow } from '../../../../imports';
import './CalendarManagement.css';

const CalendarManagement = () => {
    const { userRole } = useAuth();
    const { 
        loading,
        error,
        getDaysInMonth,
        getAvailableTimeSlots,
        getFormattedMonthYear,
        goToPrevMonth,
        goToNextMonth,
        selectedDate,
        setSelectedDate,
        defaultTimeSlots,
        updateAvailability,
        updateTimeSlotAvailability,
        bookTimeSlot,
        updateRecurringSchedule,
        getRecurringSchedule
    } = useCalendar();
    
    const [timeSlots, setTimeSlots] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [debugInfo, setDebugInfo] = useState(null);
    const [recurringSchedule, setRecurringSchedule] = useState({});
    const [selectedWeekday, setSelectedWeekday] = useState(null);
    const [isRecurringMode, setIsRecurringMode] = useState(false);
    const [hasOverrides, setHasOverrides] = useState({});

    // Load recurring schedule
    useEffect(() => {
        const loadRecurringSchedule = async () => {
            try {
                const schedule = await getRecurringSchedule();
                setRecurringSchedule(schedule);
            } catch (err) {
                setSaveError('Failed to load recurring schedule');
            }
        };
        loadRecurringSchedule();
    }, [getRecurringSchedule]);

    // Load time slots when selected date changes
    useEffect(() => {
        if (selectedDate) {
            try {
                const availableSlots = getAvailableTimeSlots(selectedDate);
                setTimeSlots(availableSlots);
                
                // Parse date in local timezone
                const [year, month, day] = selectedDate.split('-').map(Number);
                const dateObj = new Date(year, month - 1, day);
                dateObj.setHours(0, 0, 0, 0);
                
                // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
                const dayOfWeek = dateObj.getDay();
                const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const weekday = weekdays[dayOfWeek];
                
                const hasDateOverrides = availableSlots.some(slot => {
                    const recurringValue = recurringSchedule[weekday]?.[slot.time];
                    return slot.available !== recurringValue;
                });
                
                setHasOverrides(prev => ({
                    ...prev,
                    [selectedDate]: hasDateOverrides
                }));
            } catch (err) {
                setTimeSlots([]);
                setSaveError('Failed to load time slots');
            }
        } else {
            setTimeSlots([]);
        }
    }, [selectedDate, getAvailableTimeSlots, recurringSchedule]);

    // Reset save status after 3 seconds
    useEffect(() => {
        if (saveSuccess || saveError) {
            const timer = setTimeout(() => {
                setSaveSuccess(false);
                setSaveError(null);
            }, 3000);
            
            return () => clearTimeout(timer);
        }
    }, [saveSuccess, saveError]);

    const handleDateSelect = (day) => {
        if (day.date && !day.isPast) {
            setSelectedDate(day.date);
        }
    };

    const handleDateAvailability = async (day) => {
        if (!day.date || userRole !== 'admin' || day.isPast) return;
        
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        setDebugInfo(null);
        
        try {
            // Toggle availability for the date
            const result = await updateAvailability(day.date, !day.available);
            
            if (!result.success) {
                throw new Error(result.error.message);
            }
            
            setSaveSuccess(true);
        } catch (err) {
            setSaveError(err.message);
            setDebugInfo(JSON.stringify(err, null, 2));
        } finally {
            setIsSaving(false);
        }
    };

    const handleTimeSlotAvailability = async (time) => {
        if (!selectedDate || userRole !== 'admin') return;
        
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        setDebugInfo(null);
        
        try {
            // Find the current time slot
            const currentSlot = timeSlots.find(slot => slot.time === time);
            if (!currentSlot) return;
            
            // Parse date in local timezone
            const [year, month, day] = selectedDate.split('-').map(Number);
            const dateObj = new Date(year, month - 1, day);
            dateObj.setHours(0, 0, 0, 0);
            
            // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
            const dayOfWeek = dateObj.getDay();
            const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const weekday = weekdays[dayOfWeek];
            
            // Check if this slot is enabled in the recurring schedule
            const isRecurringEnabled = recurringSchedule[weekday]?.[time] === true;
            
            // Toggle availability for the time slot
            const result = await updateTimeSlotAvailability(
                selectedDate, 
                time, 
                !currentSlot.available
            );
            
            if (!result.success) {
                throw new Error(result.error.message);
            }
            
            // Update local state
            setTimeSlots(prev => prev.map(slot => 
                slot.time === time 
                    ? { ...slot, available: !slot.available }
                    : slot
            ));
            
            setSaveSuccess(true);
        } catch (err) {
            setSaveError(err.message);
            setDebugInfo(JSON.stringify(err, null, 2));
        } finally {
            setIsSaving(false);
        }
    };

    const handleRecurringScheduleUpdate = async (weekday, timeSlot, isAvailable) => {
        if (userRole !== 'admin') return;
        
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        
        try {
            const result = await updateRecurringSchedule(weekday, timeSlot, isAvailable);
            
            if (!result.success) {
                throw new Error(result.error.message);
            }
            
            // Update local state
            setRecurringSchedule(prev => ({
                ...prev,
                [weekday]: {
                    ...prev[weekday],
                    [timeSlot]: isAvailable
                }
            }));
            
            setSaveSuccess(true);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const days = getDaysInMonth();
    const monthYear = getFormattedMonthYear();
    const weekDays = [
        { key: 'sunday', label: 'Sun' },
        { key: 'monday', label: 'Mon' },
        { key: 'tuesday', label: 'Tue' },
        { key: 'wednesday', label: 'Wed' },
        { key: 'thursday', label: 'Thu' },
        { key: 'friday', label: 'Fri' },
        { key: 'saturday', label: 'Sat' }
    ];

    const hasAdminAccess = userRole === 'admin';

    if (!hasAdminAccess) {
        return (
            <div className="calendar-management">
                <h2>Manage Schedule</h2>
                <div className="calendar-management__unauthorized">
                    <p>You do not have permission to access this page.</p>
                    <p>Current role: {userRole || 'none'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="calendar-management">
            <h2>Manage Schedule</h2>
            
            {loading && <div className="calendar-management__loading">Loading calendar data...</div>}
            {error && <div className="calendar-management__error">Error: {error}</div>}
            {isSaving && <div className="calendar-management__saving">Saving changes...</div>}
            {saveSuccess && <div className="calendar-management__success">Changes saved successfully!</div>}
            {saveError && <div className="calendar-management__error">Error saving changes: {saveError}</div>}
            {debugInfo && (
                <div className="calendar-management__debug">
                    <details>
                        <summary>Debug Information</summary>
                        <pre>{debugInfo}</pre>
                    </details>
                </div>
            )}
            
            <div className="calendar-management__container">
                <div className="calendar-management__dates">
                    <div className="calendar-header">
                        <button onClick={goToPrevMonth} className="calendar-nav-button">
                            <PrevArrow />
                        </button>
                        <span>{monthYear}</span>
                        <button onClick={goToNextMonth} className="calendar-nav-button">
                            <NextArrow />
                        </button>
                    </div>
                    
                    <div className="calendar-weekdays">
                        {weekDays.map(day => (
                            <div key={day.key} className="weekday">{day.label}</div>
                        ))}
                    </div>
                    
                    <div className="calendar-days">
                        {days.map((day, index) => (
                            <div
                                key={`${day.day}-${index}`}
                                className={`calendar-day ${!day.day ? 'empty' : ''} 
                                          ${day.available ? 'available' : ''} 
                                          ${selectedDate === day.date ? 'selected' : (day.isToday ? 'today' : '')}
                                          ${day.disabled ? 'disabled' : ''}
                                          ${day.isPast ? 'past' : ''}
                                          ${hasOverrides[day.date] ? 'has-override' : ''}`}
                                onClick={() => handleDateSelect(day)}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    handleDateAvailability(day);
                                }}
                            >
                                {day.day}
                                {hasOverrides[day.date] && (
                                    <span className="override-indicator" title="Has custom schedule">*</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                    <div className="calendar-management__times">
                    <div className="schedule-mode-toggle">
                        <button 
                            className={`mode-button ${!isRecurringMode ? 'active' : ''}`}
                            onClick={() => setIsRecurringMode(false)}
                        >
                            Daily Schedule
                        </button>
                        <button 
                            className={`mode-button ${isRecurringMode ? 'active' : ''}`}
                            onClick={() => setIsRecurringMode(true)}
                        >
                            Recurring Schedule
                        </button>
                    </div>

                    {isRecurringMode ? (
                        <div className="recurring-schedule">
                            <h3>Recurring Schedule</h3>
                            <div className="weekday-selector">
                                {weekDays.map(day => (
                                    <button
                                        key={day.key}
                                        className={`weekday-button ${selectedWeekday === day.key ? 'selected' : ''}`}
                                        onClick={() => setSelectedWeekday(day.key)}
                                    >
                                        {day.label}
                                    </button>
                                ))}
                            </div>

                            {selectedWeekday && (
                                <div className="recurring-time-slots">
                                    <h4>{weekDays.find(d => d.key === selectedWeekday)?.label} Schedule</h4>
                                    <div className="time-slots">
                                        {defaultTimeSlots.map(slot => (
                                            <div
                                                key={slot}
                                                className={`time-slot ${
                                                    recurringSchedule[selectedWeekday]?.[slot] ? 'available' : ''
                                                }`}
                                                onClick={() => handleRecurringScheduleUpdate(
                                                    selectedWeekday,
                                                    slot,
                                                    !recurringSchedule[selectedWeekday]?.[slot]
                                                )}
                                            >
                                                {slot}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        selectedDate && (
                            <div className="daily-schedule">
                        <h3>Time Slots for {formatDisplayDate(selectedDate)}</h3>
                                {hasOverrides[selectedDate] && (
                                    <div className="override-notice">
                                        This date has custom time slots that override the recurring schedule
                                    </div>
                                )}
                        <div className="time-slots">
                            {timeSlots.map(slot => (
                                <div
                                    key={slot.time}
                                    className={`time-slot ${slot.available ? 'available' : ''}`}
                                    onClick={() => handleTimeSlotAvailability(slot.time)}
                                >
                                    {slot.time}
                                </div>
                            ))}
                        </div>
                    </div>
                        )
                )}
                </div>
            </div>

            <div className="calendar-management__instructions">
                <p>Instructions:</p>
                <ul>
                    <li>Click a date to view/edit its time slots</li>
                    <li>Right-click a date to toggle its availability</li>
                    <li>Click a time slot to toggle its availability</li>
                    <li>Use the Recurring Schedule mode to set up weekly recurring availability</li>
                    <li>Select a day of the week to manage its recurring time slots</li>
                    <li>Dates marked with * have custom schedules that override the recurring schedule</li>
                </ul>
            </div>
        </div>
    );
};

export default CalendarManagement;